import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { AcsButton, AcsCard, AcsInput, AcsLogo, AcsPageShell } from '../components';
import { LanguageSwitcher } from '../components/LanguageSwitcher';
import { useAuth } from '../auth/AuthProvider';
import { useI18n } from '../i18n/I18nProvider';
import { changeOwnPassword } from '../api/client';

/**
 * Il proprio account (§10.2).
 *
 * Raggiungibile da chiunque sia autenticato, non solo dagli ADMIN: senza,
 * l'unico modo di cambiare una password sarebbe il reset amministrativo, e chi
 * amministra conoscerebbe per sempre la password di ogni collega. Una password
 * generata da altri e mai sostituibile non è un segreto personale.
 */
export function AccountPage() {
  const { t } = useI18n();
  const auth = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: () => changeOwnPassword(current, next),
    onSuccess: () => {
      setCurrent('');
      setNext('');
      setConfirm('');
      setError(null);
      setDone(true);
    },
    onError: (cause: Error) => setError(cause.message),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (next.length < 12) return setError(t.account.minLength);
    if (next !== confirm) return setError(t.account.mismatch);
    change.mutate();
  }

  return (
    <AcsPageShell
      header={
        <>
          <AcsLogo />
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--acs-space-4)' }}>
            <LanguageSwitcher />
            <Link to="/app" className="acs-button acs-button--secondary">
              {t.app.events}
            </Link>
          </div>
        </>
      }
    >
      <h1 style={{ fontSize: 'var(--acs-font-size-xl)' }}>{t.account.title}</h1>
      {auth.status === 'authenticated' ? (
        <p style={{ color: 'var(--acs-color-text-muted)' }}>{auth.user.email}</p>
      ) : null}

      <AcsCard>
        <h2 style={{ marginTop: 0 }}>{t.account.passwordSection}</h2>
        <p style={{ color: 'var(--acs-color-text-muted)' }}>{t.account.passwordHint}</p>

        {done ? (
          <p role="status">{t.account.changed}</p>
        ) : (
          <form onSubmit={submit} className="acs-stack">
            <AcsInput
              label={t.account.currentPassword}
              type="password"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              autoComplete="current-password"
              required
            />
            <AcsInput
              label={t.account.newPassword}
              type="password"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              autoComplete="new-password"
              required
            />
            <AcsInput
              label={t.account.confirmPassword}
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password"
              required
            />

            {error ? (
              <p className="acs-field__error" role="alert">
                {error}
              </p>
            ) : null}

            <div>
              <AcsButton type="submit" disabled={change.isPending}>
                {t.account.submit}
              </AcsButton>
            </div>
          </form>
        )}
      </AcsCard>
    </AcsPageShell>
  );
}
